// Report packets (legacy desktop Reports ▸ Process Multiple Reports + E-mail report).
// Pick reports + one period → one PDF (cover page + each report) or an email with the same tables.
// Saved packets keep the report list and the usual recipients (e.g. the board's monthly packet).
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { PRESETS } from '../reportCatalog';
import { FileDown, Mail, Save, Trash2 } from 'lucide-react';

const field = 'h-9 rounded border border-slate-300 dark:border-slate-600 bg-transparent px-2 text-sm';
const errOf = (e) => e?.response?.data?.detail || 'Request failed';

export function PacketPanel({ base, CAT, defaults, card }) {
  const options = useMemo(() => {
    const seen = new Set();
    return CAT.filter((d) => !d.params && !seen.has(d.k) && seen.add(d.k)).map((d) => ({ report: d.k, label: d.label, g: d.g }));
  }, [CAT]);
  const labelOf = (k) => (options.find((o) => o.report === k) || {}).label || k;
  const [sel, setSel] = useState(defaults);
  const [title, setTitle] = useState('Board packet');
  const [preset, setPreset] = useState('last_month'); const [start, setStart] = useState(''); const [end, setEnd] = useState('');
  const [to, setTo] = useState(''); const [note, setNote] = useState('');
  const [saved, setSaved] = useState([]); const [pid, setPid] = useState('');
  const [busy, setBusy] = useState(''); const [msg, setMsg] = useState(''); const [err, setErr] = useState('');
  const loadSaved = () => axios.get(`${base}/report-packets`).then((r) => setSaved(r.data || [])).catch(() => {});
  useEffect(() => { loadSaved(); /* eslint-disable-next-line */ }, [base]);
  const body = () => ({ title, reports: sel.map((k) => ({ report: k, label: labelOf(k) })),
    params: preset === 'custom' ? { preset, start, end } : { preset } });
  const toggle = (k) => setSel((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const open = (p) => { setPid(p.id); setTitle(p.name); setSel(p.reports.map((r) => r.report)); setTo((p.recipients || []).join(', ')); };
  const pdf = async () => {
    setBusy('pdf'); setErr(''); setMsg('');
    try {
      const r = await axios.post(`${base}/report-packets/build`, body(), { responseType: 'blob' });
      window.open(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank');
    } catch (e) { setErr(e?.response?.status === 400 ? 'Choose at least one report' : errOf(e)); } finally { setBusy(''); }
  };
  const email = async () => {
    setBusy('email'); setErr(''); setMsg('');
    try {
      const r = await axios.post(`${base}/report-packets/email`, { ...body(), to, note });
      setMsg(r.data.sent.map((s) => `${s.to}: ${s.status === 'sent' ? 'sent' : 'not sent (logged only)'}`).join(' · '));
    } catch (e) { setErr(errOf(e)); } finally { setBusy(''); }
  };
  const save = async () => {
    setErr(''); setMsg('');
    try {
      const payload = { name: title, reports: body().reports, recipients: to };
      const r = pid ? await axios.put(`${base}/report-packets/${pid}`, payload) : await axios.post(`${base}/report-packets`, payload);
      setPid(r.data.id); setMsg('Packet saved'); loadSaved();
    } catch (e) { setErr(errOf(e)); }
  };
  const del = async (p) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the packet "${p.name}"?`)) return;
    await axios.delete(`${base}/report-packets/${p.id}`).catch(() => {}); if (pid === p.id) setPid(''); loadSaved();
  };
  return (
    <div className={`rounded-lg border ${card} p-3 space-y-3`} data-testid="report-packet">
      <p className="text-sm text-slate-500">Run several reports for one period as a single PDF (cover page first) or email them — e.g. the board&apos;s monthly packet.</p>
      {saved.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-xs text-slate-500">Saved packets:</span>
          {saved.map((p) => (
            <span key={p.id} className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 ${pid === p.id ? 'border-emerald-500' : 'border-slate-300'}`}>
              <button className="text-emerald-600 hover:underline" onClick={() => open(p)} data-testid={`packet-open-${p.id}`}>{p.name}</button>
              <button title="Delete" onClick={() => del(p)} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-3 w-3" /></button>
            </span>))}
        </div>)}
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-slate-500">Packet name<Input className={`${field} w-56`} value={title} onChange={(e) => setTitle(e.target.value)} data-testid="packet-title" /></label>
        <label className="text-xs text-slate-500">Period
          <select className={`${field} block`} value={preset} onChange={(e) => setPreset(e.target.value)} data-testid="packet-preset">
            {PRESETS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select></label>
        {preset === 'custom' && <>
          <label className="text-xs text-slate-500">From<Input type="date" className={field} value={start} onChange={(e) => setStart(e.target.value)} /></label>
          <label className="text-xs text-slate-500">To<Input type="date" className={field} value={end} onChange={(e) => setEnd(e.target.value)} /></label></>}
      </div>
      <div className="max-h-56 overflow-auto rounded border border-slate-200 dark:border-slate-700 p-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4" data-testid="packet-reports">
        {options.map((o) => (
          <label key={o.report} className="flex items-center gap-2 text-sm py-0.5">
            <input type="checkbox" checked={sel.includes(o.report)} onChange={() => toggle(o.report)} data-testid={`packet-pick-${o.report}`} />{o.label}
          </label>))}
      </div>
      <p className="text-xs text-slate-500">In the packet, in this order: {sel.map(labelOf).join(' · ') || 'none chosen'}</p>
      <div className="flex flex-wrap items-end gap-3">
        <Button className="bg-emerald-600 text-white" disabled={!sel.length || !!busy} onClick={pdf} data-testid="packet-pdf"><FileDown className="h-4 w-4 mr-1" />{busy === 'pdf' ? 'Building…' : 'Download PDF'}</Button>
        <label className="text-xs text-slate-500 flex-1 min-w-[240px]">Email to (commas between)<Input className={`${field} w-full`} value={to} onChange={(e) => setTo(e.target.value)} placeholder="board@example.org" data-testid="packet-to" /></label>
        <label className="text-xs text-slate-500 flex-1 min-w-[200px]">Message<Input className={`${field} w-full`} value={note} onChange={(e) => setNote(e.target.value)} data-testid="packet-note" /></label>
        <Button variant="outline" disabled={!sel.length || !to.trim() || !!busy} onClick={email} data-testid="packet-email"><Mail className="h-4 w-4 mr-1" />{busy === 'email' ? 'Sending…' : 'Email'}</Button>
        <Button variant="outline" disabled={!sel.length || !title.trim()} onClick={save} data-testid="packet-save"><Save className="h-4 w-4 mr-1" />{pid ? 'Update packet' : 'Save packet'}</Button>
      </div>
      {msg && <p className="text-sm text-emerald-700" data-testid="packet-msg">{msg}</p>}
      {err && <p className="text-sm text-rose-600">{err}</p>}
    </div>
  );
}
